import { createAsyncThunk } from '@reduxjs/toolkit';

import callApi from '../../service/callApi';
import { enqueueSnackbar, somethingWrongNotification } from '../notifier/notifierActions';
import { history } from '../../store';
import sendRequest from '../../service/sendRequest';
import { AGENT_TYPES, PROJECT_TYPES, PROJECT_TYPES_API } from '../../constants';

const GET_ACCESS_USERS = 'projects/GET_ACCESS_USERS';
const LOAD_MORE_ACCESS_USERS = 'projects/LOAD_MORE_ACCESS_USERS';
const GET_FILTER_USERS = 'projects/GET_FILTER_USERS';
const LOAD_MORE_FILTER_USERS = 'projects/LOAD_MORE_FILTER_USERS';
const GET_PROJECTS = 'projects/GET_PROJECTS';
const CREATE_RESONANCE_PROJECT = 'projects/CREATE_RESONANCE_PROJECT';
const CREATE_SCOUT_PROJECT = 'projects/CREATE_SCOUT_PROJECT';
const CREATE_GENIE_PROJECT = 'projects/CREATE_GENIE_PROJECT';
const CREATE_GENIE_GO_PROJECT = 'projects/CREATE_GENIE_GO_PROJECT';
const CREATE_ID8_PROJECT = 'projects/CREATE_ID8_PROJECT';
const CREATE_ACTIV8_PROJECT = 'projects/CREATE_ACTIV8_PROJECT';
const UPLOAD_PROJECT_FILE = 'projects/UPLOAD_PROJECT_FILE';
const DELETE_PROJECT = 'projects/DELETE_PROJECT';
const DEACTIVATE_PROJECT = 'projects/DEACTIVATE_PROJECT';
const GET_PROJECT = 'projects/GET_PROJECT';
const CHANGE_SONAR_PROJECT = 'projects/CHANGE_SONAR_PROJECT';
const CHANGE_SHARING_PROJECT = 'projects/CHANGE_SHARING_PROJECT';
const CHANGE_PROJECT_FILE = 'projects/CHANGE_PROJECT_FILE';
const CHANGE_SHARE_PASSWORD = 'projects/CHANGE_SHARE_PASSWORD';
const CHANGE_FINAL_MESSAGES_PASSWORD = 'projects/CHANGE_FINAL_MESSAGES_PASSWORD';
const CHANGE_PROJECT_ACCESS_USERS = 'projects/CHANGE_PROJECT_ACCESS_USERS';
const CHANGE_PROJECT = 'projects/CHANGE_PROJECT';
const GET_ACCESS_ASSISTANTS = 'scout/GET_ACCESS_ASSISTANTS';
const CHANGE_SCOUT_PROJECT_ACCESS_ASSISTANTS = 'projects/CHANGE_SCOUT_PROJECT_ACCESS_ASSISTANTS';
const CHANGE_GENIE_PROJECT_ACCESS_ASSISTANTS = 'projects/CHANGE_GENIE_PROJECT_ACCESS_ASSISTANTS';

// * ACCESS USERS
const loadAccessUsers = async ({ search, page = 1 }) => {
  const response = await sendRequest(() =>
    callApi.get(`/api/user/management/search?${search ? 'query=' + search + '&' : ''}page=${page}&size=${10}`),
  );

  return { content: response.data.content, total: response.data.totalElements };
};
export const getAccessUsers = createAsyncThunk(GET_ACCESS_USERS, loadAccessUsers);
export const loadMoreAccessUsers = createAsyncThunk(LOAD_MORE_ACCESS_USERS, loadAccessUsers);

// * ACCESS ASSISTANTS
export const getAccessAssistants = createAsyncThunk(GET_ACCESS_ASSISTANTS, async (_, { rejectWithValue }) => {
  const response = await sendRequest(() => callApi.get(`/api/assistant/find/all`), {
    onError: (e) => {
      return rejectWithValue(e);
    },
  });

  return response.data;
});

// * FILTER USERS
const loadFilterUsers = async ({ search, page = 1 }) => {
  const response = await sendRequest(() =>
    callApi.get(`/api/user/management/admins?${search ? 'query=' + search + '&' : ''}page=${page}&size=${10}`),
  );

  return { content: response.data.content, total: response.data.totalElements };
};
export const getFilterUsers = createAsyncThunk(GET_FILTER_USERS, loadFilterUsers);
export const loadMoreFilterUsers = createAsyncThunk(LOAD_MORE_FILTER_USERS, loadFilterUsers);

export const getProjects = createAsyncThunk(GET_PROJECTS, async (_, { getState }) => {
  const { page, size, sort, projectType, createdBy, searchQuery } = getState().projects.tableSettings;
  const createdByValue = createdBy.value;

  const response = await sendRequest(() =>
    callApi.post(`/api/project/search?page=${page + 1}&size=${size}&sort=${sort}`, {
      ...(projectType ? { projectType: projectType } : {}),
      ...(createdByValue ? { createdBy: createdByValue } : {}),
      ...(searchQuery ? { query: searchQuery } : {}),
    }),
  );

  return { content: response.data.content, total: response.data.totalElements };
});

export const createResonanceProject = createAsyncThunk(
  CREATE_RESONANCE_PROJECT,
  async ({ projectName, activeUsers, file, resonanceTheme, vision, dataStructure, lang }, { dispatch }) => {
    const response = await sendRequest(
      () =>
        callApi.post(`/api/resonance`, {
          projectName,
          userIds: activeUsers.map((user) => user.id),
          theme: resonanceTheme,
          vision,
          dataStructure,
          lang,
        }),
      {
        successStatusCode: 201,
        onError: (e) => createProjectErrorHandler(e, projectName, dispatch),
      },
    );

    const id = response.data;
    dispatch(uploadProjectFile({ id, file, projectName, projectType: PROJECT_TYPES.RESONANCE }));
  },
);

export const createScoutProject = createAsyncThunk(
  CREATE_SCOUT_PROJECT,
  async (
    { projectName, activeUsers, activeAllAssistants, activeSelectedAssistants, activeCompressAssistants },
    { dispatch },
  ) => {
    await sendRequest(
      () =>
        callApi.post(`/api/scout-v2`, {
          projectName,
          userIds: activeUsers.map((user) => user.id),
          assistantIds: [
            ...activeAllAssistants.map(({ id }) => id),
            ...activeSelectedAssistants.map(({ id }) => id),
            ...activeCompressAssistants.map(({ id }) => id),
          ],
        }),
      {
        successStatusCode: 201,
        onError: (e) => createProjectErrorHandler(e, projectName, dispatch),
      },
    );

    createProjectSuccessHandler(projectName, dispatch);
  },
);

export const createGenieProject = createAsyncThunk(
  CREATE_GENIE_PROJECT,
  async ({ projectName, activeUsers, brand, activeAllAssistants }, { dispatch }) => {
    await sendRequest(
      () =>
        callApi.post(`/api/genie`, {
          projectName,
          brand,
          userIds: activeUsers.map((user) => user.id),
          assistantIds: [...activeAllAssistants.map(({ id }) => id)],
        }),
      {
        successStatusCode: 201,
        onError: (e) => createProjectErrorHandler(e, projectName, dispatch),
      },
    );

    createProjectSuccessHandler(projectName, dispatch);
  },
);

export const createGenieGoProject = createAsyncThunk(
  CREATE_GENIE_GO_PROJECT,
  async ({ projectName, activeUsers, brand, activeAllAssistants, description, referenceMessages }, { dispatch }) => {
    await sendRequest(
      () =>
        callApi.post(`/api/genie2go`, {
          projectName,
          brand,
          userIds: activeUsers.map((user) => user.id),
          assistantIds: [...activeAllAssistants.map(({ id }) => id)],
          description,
          referenceMessages,
        }),
      {
        successStatusCode: 201,
        onError: (e) => createProjectErrorHandler(e, projectName, dispatch),
      },
    );

    createProjectSuccessHandler(projectName, dispatch);
  },
);

export const createId8Project = createAsyncThunk(
  CREATE_ID8_PROJECT,
  async ({ projectName, activeUsers, thinking, agentId, instructions, agentType }, { dispatch, rejectWithValue }) => {
    let requestedAgentId = agentId;

    if (agentType === AGENT_TYPES.CUSTOM) {
      const agentResponse = await sendRequest(() => callApi.put(`/api/id8/agent/register/custom`, { instructions }), {
        onError: (e) => {
          dispatch(
            enqueueSnackbar({
              message: 'couldntCreateAgent',
              options: { variant: 'error' },
            }),
          );
          return rejectWithValue(e);
        },
      });

      requestedAgentId = agentResponse.data.id;
    }

    await sendRequest(
      () =>
        callApi.post(`/api/id8`, {
          projectName,
          userIds: activeUsers.map((user) => user.id),
          thinking,
          agentId: requestedAgentId,
        }),
      {
        successStatusCode: 201,
        onError: (e) => createProjectErrorHandler(e, projectName, dispatch),
      },
    );

    createProjectSuccessHandler(projectName, dispatch);
  },
);

export const uploadProjectFile = createAsyncThunk(
  UPLOAD_PROJECT_FILE,
  async ({ id, file, projectName, projectType }, { dispatch, rejectWithValue }) => {
    if (!id || !file || !projectType) {
      return rejectWithValue(null);
    }

    const formData = new FormData();
    formData.append('file', file, file.name);

    await sendRequest(
      () =>
        callApi.request({
          url: `/api/${PROJECT_TYPES_API[projectType]}/add-file/${id}`,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          method: 'POST',
          data: formData,
        }),
      { onError: (e) => rejectWithValue(e) },
    );

    createProjectSuccessHandler(projectName, dispatch);
  },
);

export const deleteProject = createAsyncThunk(DELETE_PROJECT, async ({ id, projectType }, { dispatch }) => {
  await sendRequest(() => callApi.delete(`/api/${projectType}/${id}`));

  dispatch(
    enqueueSnackbar({
      message: 'projectHasBeenDeleted',
      options: {
        variant: 'success',
      },
    }),
  );
  dispatch(getProjects());
});

export const getProject = createAsyncThunk(GET_PROJECT, async ({ id }) => {
  const response = await sendRequest(() => callApi.get(`/api/project/edit-info/${id}`));

  return response.data;
});

export const deactivateProject = createAsyncThunk(DEACTIVATE_PROJECT, async ({ id, active }, { dispatch }) => {
  const response = await sendRequest(() => callApi.post(`/api/project/${active ? 'deactivate' : 'activate'}/${id}`));

  dispatch(
    enqueueSnackbar({
      message: 'projectHasBeenDeactivated',
      options: {
        variant: 'success',
      },
    }),
  );

  return response.data;
});

export const changeSonarProject = createAsyncThunk(CHANGE_SONAR_PROJECT, async ({ projectName, id }, { dispatch }) => {
  const response = await sendRequest(() => callApi.post(`/api/project/edit/sonar/${id}`, { projectName }));

  dispatch(
    enqueueSnackbar({
      message: 'projectHasBeenChanged',
      options: {
        variant: 'success',
      },
    }),
  );

  return response.data;
});

export const changeProject = createAsyncThunk(CHANGE_PROJECT, async ({ id, projectType, values }, { dispatch }) => {
  const response = await sendRequest(() =>
    callApi.post(`/api/${PROJECT_TYPES_API[projectType]}/edit/${id}`, { ...values }),
  );

  dispatch(
    enqueueSnackbar({
      message: 'projectHasBeenChanged',
      options: {
        variant: 'success',
      },
    }),
  );

  return response.data;
});

export const changeProjectFile = createAsyncThunk(
  CHANGE_PROJECT_FILE,
  async ({ id, file, projectType }, { dispatch, rejectWithValue }) => {
    if (!id || !file || !projectType) {
      return rejectWithValue(null);
    }

    const formData = new FormData();
    formData.append('file', file, file.name);

    const response = await sendRequest(
      () =>
        callApi.request({
          url: `/api/${PROJECT_TYPES_API[projectType]}/add-file/${id}`,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          method: 'POST',
          data: formData,
        }),
      {
        onError: (e) => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(e);
        },
      },
    );

    if (response.status === 200) {
      dispatch(
        enqueueSnackbar({
          message: 'projectFileHasBeenChanged',
          options: {
            variant: 'success',
          },
        }),
      );
    }
  },
);

export const changeProjectSharing = createAsyncThunk(CHANGE_SHARING_PROJECT, async ({ id, activeSharing }) => {
  const response = await sendRequest(() =>
    callApi.post(`/api/project/${activeSharing ? 'activate' : 'deactivate'}/sharing/${id}`),
  );

  return response.data;
});

export const changeSharePassword = createAsyncThunk(
  CHANGE_SHARE_PASSWORD,
  async ({ id, projectPassword }, { dispatch }) => {
    const response = await sendRequest(() => callApi.post(`/api/project/edit/share/${id}`, { projectPassword }));

    dispatch(
      enqueueSnackbar({
        message: 'projectPasswordHasBeenChanged',
        options: {
          variant: 'success',
        },
      }),
    );

    return response.data;
  },
);

export const changeFinalMessagesPassword = createAsyncThunk(
  CHANGE_FINAL_MESSAGES_PASSWORD,
  async ({ id, finalMessagesPassword }, { dispatch }) => {
    const response = await sendRequest(() => callApi.post(`/api/scout-v2/edit/share/${id}`, { finalMessagesPassword }));

    dispatch(
      enqueueSnackbar({
        message: 'finalMessagesPasswordHasBeenChanged',
        options: {
          variant: 'success',
        },
      }),
    );

    return response.data;
  },
);

export const changeProjectAccessUsers = createAsyncThunk(
  CHANGE_PROJECT_ACCESS_USERS,
  async ({ id, userIds }, { dispatch }) => {
    const response = await sendRequest(() => callApi.post(`/api/project/edit/users/${id}`, { userIds }));

    dispatch(
      enqueueSnackbar({
        message: 'projectAssignedUsersHasBeenChanged',
        options: {
          variant: 'success',
        },
      }),
    );

    return response.data;
  },
);

export const changeScoutProjectAccessAssistants = createAsyncThunk(
  CHANGE_SCOUT_PROJECT_ACCESS_ASSISTANTS,
  async (
    { id, activeAllAssistants, activeSelectedAssistants, activeCompressAssistants },
    { dispatch, rejectWithValue },
  ) => {
    if (!id) {
      return rejectWithValue(null);
    }

    const response = await sendRequest(
      () =>
        callApi.post(`/api/scout-v2/edit/assistants?id=${id}`, {
          assistantIds: [
            ...activeAllAssistants.map(({ id }) => id),
            ...activeSelectedAssistants.map(({ id }) => id),
            ...activeCompressAssistants.map(({ id }) => id),
          ],
        }),
      {
        onError: (e) => {
          return rejectWithValue(e);
        },
      },
    );

    dispatch(
      enqueueSnackbar({
        message: 'projectAssignedAssistantsHasBeenChanged',
        options: {
          variant: 'success',
        },
      }),
    );

    return response.data;
  },
);

export const changeGenieProjectAccessAssistants = createAsyncThunk(
  CHANGE_GENIE_PROJECT_ACCESS_ASSISTANTS,
  async ({ id, activeAllAssistants, projectType }, { dispatch, rejectWithValue }) => {
    if (!id) {
      return rejectWithValue(null);
    }

    const response = await sendRequest(
      () =>
        callApi.post(`/api/${PROJECT_TYPES_API[projectType]}/edit/assistants?id=${id}`, {
          assistantIds: [...activeAllAssistants.map(({ id }) => id)],
        }),
      {
        onError: (e) => {
          return rejectWithValue(e);
        },
      },
    );

    dispatch(
      enqueueSnackbar({
        message: 'projectAssignedAssistantsHasBeenChanged',
        options: {
          variant: 'success',
        },
      }),
    );

    return response.data;
  },
);

// ACTIV8
export const createActiv8Project = createAsyncThunk(
  CREATE_ACTIV8_PROJECT,
  async ({ projectName, activeUsers }, { dispatch }) => {
    await sendRequest(
      () =>
        callApi.post(`/api/activ8`, {
          projectName,
          userIds: activeUsers.map((user) => user.id),
        }),
      {
        successStatusCode: 201,
        onError: (e) => createProjectErrorHandler(e, projectName, dispatch),
      },
    );

    createProjectSuccessHandler(projectName, dispatch);
  },
);

// DEFAULTS
const createProjectErrorHandler = (event, projectName, dispatch) => {
  if (event.response.status === 409) {
    dispatch(
      enqueueSnackbar({
        message: 'projectNameAlreadyExist',
        options: {
          variant: 'warning',
          messageParams: {
            projectName,
          },
        },
      }),
    );
  } else {
    dispatch(somethingWrongNotification());
  }
};

const createProjectSuccessHandler = (projectName, dispatch) => {
  dispatch(
    enqueueSnackbar({
      message: 'projectHasBeenCreated',
      options: {
        variant: 'success',
        messageParams: {
          projectName,
        },
      },
    }),
  );

  history.replace('/projects');
};
